export const populateActionChoicesActions = (actions) =>
  (actions || []).reduce((acc, action) => {
    acc[action.task] = action;
    return acc;
  }, {});

export const populateActionChoicesTypes = (actionTypes = []) => ({
  prevention: {
    id: actionTypes[0][0],
    name: actionTypes[0][1],
  },
  treatment: {
    id: actionTypes[1][0],
    name: actionTypes[1][1],
  },
});

const TYPE_VARIANTS_SUFFIX = "Types";

// Normalizes an operation's technique/variant options into a single
// { typeVariants, typeVariantFieldName } shape so the modal's technique
// field can work generically across all tasks. Two sources feed this:
// - actionTechniques: the catalog-driven technique list (e.g. pheromone
//   confusion, biostimulants), each entry also carrying the actionIdentifier
//   it maps back to (used downstream to resolve the agrochemical/fertilizer
//   filter flag).
// - any "*Types"-suffixed array (e.g. ploughingTypes, mulchTypes) - the
//   older, task-specific variant lists.
const applyOperationTypeVariants = (operation) => {
  if (!operation || typeof operation !== "object") return operation;

  if (operation.actionTechniques?.length > 0) {
    const { actionTechniques, ...rest } = operation;
    return {
      ...rest,
      typeVariants: actionTechniques.map((technique) => ({
        ...technique,
        value: technique.actionTechniqueId ?? technique.actionTechnique,
        label: technique.actionTechniqueName,
      })),
      typeVariantFieldName: "actionTechniqueId",
    };
  }

  const typesEntry = Object.entries(operation).find(
    ([key, value]) =>
      key.endsWith(TYPE_VARIANTS_SUFFIX) &&
      Array.isArray(value) &&
      value.length > 0,
  );
  if (!typesEntry) return operation;

  const [typesKey, typesArray] = typesEntry;
  const typeVariantFieldName = typesKey.slice(0, -1); // "ploughingTypes" -> "ploughingType"
  const { [typesKey]: _omit, ...rest } = operation;

  return {
    ...rest,
    typeVariants: typesArray.map((type) => ({
      value: type[typeVariantFieldName],
      label: type[`${typeVariantFieldName}Name`],
    })),
    typeVariantFieldName,
  };
};

const populateTaskOperations = (operations) =>
  (operations || []).reduce((acc, operation) => {
    acc[operation.operationType] = applyOperationTypeVariants(operation);
    return acc;
  }, {});

export const populateActionChoicesOperations = (actions = {}) =>
  Object.values(actions).reduce((acc, action) => {
    Object.assign(acc, populateTaskOperations(action?.operations));
    return acc;
  }, {});
